import { useState } from "react";
import { Info, Calculator } from "lucide-react";
import { usePal, fmtKM, FOCUS } from "./PalUI";
import PriceGate from "../../shared/PriceGate";
import OverlayWrapper from "../../shared/OverlayWrapper";
import { usePriceVisibility } from "../../context/PriceVisibilityContext";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../../../ui/tooltip";

const Scenarios = ({ e, onClose }) => {
  const { isDark, brand, airline } = usePal();
  const ink = isDark ? "text-white" : "text-[#1d1d1f]";
  const muted = isDark ? "text-white/60" : "text-black/55";
  return (
    <OverlayWrapper isDark={isDark} onClose={onClose} icon={Calculator} iconColor={brand} title={e.illustrationLabel} subtitle={e.illustrationBasis}>
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="pal-illustration">
        {e.illustrations.map((x, i) => {
          const mgWins = x.outcome === "MG prevails";
          const col = mgWins ? brand : "#34D399";
          return (
            <div key={i} className="rounded-2xl p-5 border-2" style={{ borderColor: col, backgroundColor: `${col}12` }} data-testid={`pal-illustration-${i}`}>
              <div className="k-eyebrow" style={{ color: col }}>Scenario {i + 1} · {x.outcome}</div>
              <dl className="mt-4 space-y-2 text-[14px]">
                <div className="flex justify-between"><dt className={muted}>Net revenue</dt><dd className={`tabular-nums ${ink}`}>USD {fmtKM(x.revenue)}</dd></div>
                <div className="flex justify-between"><dt className={muted}>70% share</dt><dd className={`tabular-nums ${ink}`}>USD {fmtKM(x.share)}</dd></div>
                <div className="flex justify-between"><dt className={muted}>Annual MG</dt><dd className={`tabular-nums ${ink}`}>USD {fmtKM(x.mg)}</dd></div>
              </dl>
              <div className={`mt-4 pt-4 border-t ${isDark ? "border-white/10" : "border-black/10"}`}>
                <div className={`text-[12px] ${muted}`}>{airline.shortName} receives</div>
                <div className={`text-[28px] font-semibold tabular-nums ${ink}`}>USD {fmtKM(x.palReceives)}</div>
                <div className={`text-[12px] ${muted}`}>Above the MG: USD {fmtKM(x.excess)}</div>
              </div>
            </div>
          );
        })}
      </div>
    </OverlayWrapper>
  );
};

const Card = ({ children, color, testid }) => {
  const { isDark } = usePal();
  return (
    <div className="flex-1 rounded-3xl p-6 border-2" style={{ borderColor: `${color}88`, backgroundColor: isDark ? `${color}14` : `${color}0d` }} data-testid={testid}>
      {children}
    </div>
  );
};

const PalEconomics = ({ a }) => {
  const e = a.economics;
  const { brand, ink, muted, dim, isDark, hair } = usePal();
  const { revealed, toggle } = usePriceVisibility();
  const [calc, setCalc] = useState(false);
  return (
    <PriceGate accentColor={brand} isDark={isDark} revealed={revealed} onToggle={toggle}>
      {({ revealed: shown, Toggle }) => (
        <div data-testid="pal-economics">
          <div className="flex items-center justify-between gap-4 mb-5">
            <p className={`text-[15px] font-semibold ${ink}`}>{e.principleShort}</p>
            {Toggle}
          </div>
          <div className="flex flex-col md:flex-row items-stretch gap-4">
            <Card color={brand} testid="pal-mg-hero">
              <div className="k-eyebrow" style={{ color: brand }}>Minimum guarantee</div>
              <div className={`mt-3 text-[40px] font-semibold tracking-tight leading-none tabular-nums ${ink}`}>
                {shown ? `USD ${fmtKM(e.heroYear)}` : "•••••"}
              </div>
              <div className={`mt-2 text-[13px] ${muted}`}>
                {shown ? `per year · USD ${fmtKM(e.heroCycle)} per cycle × 4` : "Reveal prices to see the guarantee"}
              </div>
              <div className={`mt-3 text-[12px] font-semibold ${dim}`}>{e.heroLabel}</div>
            </Card>
            <div className="flex md:flex-col items-center justify-center gap-1 px-2">
              <span className={`text-[13px] font-bold uppercase tracking-widest ${ink}`}>or</span>
              <span className={`text-[11px] ${dim}`}>whichever is higher</span>
            </div>
            <Card color="#34D399" testid="pal-share-hero">
              <div className="k-eyebrow text-emerald-500">Revenue share</div>
              <div className={`mt-3 text-[40px] font-semibold tracking-tight leading-none ${ink}`}>70%</div>
              <div className={`mt-2 text-[13px] ${muted} flex items-center gap-1.5`}>
                of net advertising revenue
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" aria-label="Net advertising revenue definition" data-testid="pal-net-revenue-tip" className={`rounded-full ${FOCUS}`}>
                        <Info className="w-4 h-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[280px] text-[12px]" data-testid="pal-net-revenue">{e.netRevenueTooltip}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className={`mt-3 text-[12px] font-semibold ${dim}`}>{e.shareLabel}: 70% / 30%</div>
            </Card>
          </div>

          {shown && (
            <div className={`mt-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4 pt-5 border-t ${hair}`}>
              <div>
                <div className={`text-[12px] font-semibold ${dim}`}>{e.otherLabel}</div>
                <ul className="mt-2 flex flex-wrap gap-2" data-testid="pal-mg-table">
                  {e.rows.slice(1).map((r, i) => (
                    <li key={r.allocation} className={`text-[13px] px-3 py-1.5 rounded-full border ${hair} ${ink}`} data-testid={`pal-mg-row-${i}`}>
                      <span className="font-semibold">{r.allocation}</span>
                      <span className={`tabular-nums ml-2 ${muted}`}>{fmtKM(r.mgCycle)} × {r.cycles} = {fmtKM(r.mgYear)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <button type="button" onClick={() => setCalc(true)} aria-haspopup="dialog" data-testid="pal-illustration-btn"
                className={`inline-flex items-center gap-2 h-10 px-4 rounded-full text-[13px] font-semibold text-white whitespace-nowrap flex-shrink-0 ${FOCUS}`} style={{ backgroundColor: brand }}>
                <Calculator className="w-4 h-4" aria-hidden="true" /> {e.illustrationLabel}
              </button>
            </div>
          )}

          <ul className={`mt-6 flex flex-wrap gap-x-6 gap-y-1 text-[12.5px] ${dim}`}>
            {e.footer.map((f) => <li key={f}>· {f}</li>)}
          </ul>
          {calc && <Scenarios e={e} onClose={() => setCalc(false)} />}
        </div>
      )}
    </PriceGate>
  );
};

export default PalEconomics;
